import { Spacing } from '@persona-id/design-tokens';
import { set } from 'lodash/fp';
import { FC, Fragment, ReactNode, useCallback, useMemo } from 'react';
import {
  DragDropContext,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { change } from 'redux-form';
import { COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import useOnEnhancedLearnMoreClick from 'dashboard/apps/InquiryTemplateEditor/hooks/useOnEnhancedLearnMoreClick';
import {
  INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
  MAX_ROUTES_PER_BRANCHING_FOR_NON_ENHANCED,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { getWorkflowSchemaLiveUpdated } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/selectors/form';
import { TOOLBAR_HEIGHT } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import { EDIT_CONDITIONAL_PANEL_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/constants';
import LearnMoreTooltip from 'dashboard/apps/InquiryTemplateEditor/LearnMoreTooltip';
import ConditionalConfiguration from 'dashboard/apps/InquiryTemplateEditor/WorkflowStepPanel/ConditionalConfiguration';
import EditConditionalPanel from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/EditConditionalPanel';
import ElseSection from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/ElseSection';
import HoverableDraggable from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/HoverableDraggable';
import { FULL_SCREEN_HEADER_HEIGHT_XS } from 'dashboard/layouts/ShowViewLayoutV2';
import { reorder } from 'dashboard/lib/dnd/utils';
import { generateJsonSchemaConfig } from 'dashboard/lib/workflows';
import { Feature } from 'dashboard/models/inquiry-template-version';
import { ProductFeatureName } from 'dashboard/models/organization';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import {
  newWorkflowConditional,
  WorkflowConditional,
  WorkflowElse,
  WorkflowStep,
  WorkflowStepConditional,
} from 'dashboard/models/workflow-version';
import { getUsableObjectsFLE } from 'dashboard/redux/selectors/fle-workflow-context';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import useProductFeature from 'lib/hooks/useProductFeature';
import { generateEnhancedMaxLimitReachedMessage } from 'lib/product-feature-errors';
import { buildGraph } from 'lib/workflows';

const form = INQUIRY_TEMPLATE_EDITOR_FORM_NAME;

const RouteList = styled.div<{ isDraggingOver: boolean }>`
  ${({ isDraggingOver }) => css`
    background-color: ${isDraggingOver ? COLORS.indigo10 : COLORS.white};
  `};
  transition:
    background-color 0.4s ease-in-out,
    box-shadow 0.4s ease-in-out;
`;

const ItemContainer = styled.section`
  flex: 1;
`;

const ElseWrapper = styled.div<{ withPadding: boolean }>`
  ${({ withPadding }) => css`
    padding-left: ${withPadding ? Spacing[6] : Spacing[0]};
    padding-right: ${withPadding ? Spacing[6] : Spacing[0]};
  `};
`;

interface WorkflowConfigurationConditionalProps {
  name: string;
  step: WorkflowStepConditional;
  errors: WorkflowError[];
  stepsConfig: WorkflowStep[];
  conditionalRouteToEdit: number | null;
  conditionalsEditable: boolean;
  editable: boolean;
  isLimited: boolean;
  workflowVersionId: string;
  setConditionalRouteToEdit: (routeNumber: number) => void;
}

const WorkflowConfigurationConditional: FC<WorkflowConfigurationConditionalProps> = ({
  name,
  step,
  errors,
  stepsConfig,
  conditionalRouteToEdit,
  conditionalsEditable,
  editable,
  isLimited,
  workflowVersionId,
  setConditionalRouteToEdit,
}) => {
  const { conditionals, branches } = step;
  const dispatch = useDispatch();

  const workflowSchema = useSelector<RootState, WorkflowSchema>((state) =>
    getWorkflowSchemaLiveUpdated(state),
  );

  const tokensToSteps = useMemo(() => buildGraph(stepsConfig), [stepsConfig]);
  const usableObjects = useSelector((state: RootState) =>
    getUsableObjectsFLE(state, workflowVersionId, step.token),
  );

  const { jsonSchema, tokensToDisplayValue } = useMemo(
    () =>
      generateJsonSchemaConfig({
        usableObjects: usableObjects,
      }),
    [usableObjects],
  );

  const isInquiriesEnhanced = useProductFeature(ProductFeatureName.InquiriesEnhanced);

  const handleEnhancedLearnMoreClick = useOnEnhancedLearnMoreClick();

  const handleAddNewRoute = useCallback(() => {
    const newStep: WorkflowStepConditional = {
      ...step,
      conditionals: [
        ...conditionals.slice(0, conditionals.length - 1),
        newWorkflowConditional(`Route ${conditionals.length}`),
        conditionals[conditionals.length - 1],
      ],
      branches: [...branches.slice(0, branches.length - 1), [], branches[branches.length - 1]],
    };
    dispatch(change(form, `workflowVersions.${name}`, newStep));
    setConditionalRouteToEdit(conditionals.length);
  }, [branches, conditionals, name, step, dispatch, setConditionalRouteToEdit]);

  const deleteRoute = useCallback(
    (conditionalIndex: number) => () => {
      const newStep: WorkflowStepConditional = {
        ...step,
        branches: [...branches.slice(0, conditionalIndex), ...branches.slice(conditionalIndex + 1)],
        conditionals: [
          ...conditionals.slice(0, conditionalIndex),
          ...conditionals.slice(conditionalIndex + 1),
        ],
      };

      dispatch(change(form, `workflowVersions.${name}`, newStep));
    },
    [branches, conditionals, name, step, dispatch],
  );

  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const reorderedConditionals = reorder(
      conditionals,
      result.source.index,
      result.destination.index,
    );
    const reorderedBranches = reorder(branches, result.source.index, result.destination.index);

    // update redux
    dispatch(
      change(form, `workflowVersions.${name}`, {
        ...step,
        conditionals: reorderedConditionals,
        branches: reorderedBranches,
      }),
    );
  };

  const routesQuotaMet =
    !isInquiriesEnhanced && branches.length >= MAX_ROUTES_PER_BRANCHING_FOR_NON_ENHANCED;

  const conditionalSections: ReactNode[] = useMemo(
    () =>
      conditionals.map((conditional, index) => {
        if (index === conditionals.length - 1) {
          return null;
        }
        const routeNumber = index + 1;
        return (
          <Fragment key={conditional.token}>
            <HoverableDraggable
              index={index}
              draggableId={conditional.token}
              isDragDisabled={!editable}
            >
              <ItemContainer>
                <ConditionalConfiguration
                  // allow inquiries basic to edit conditional criteria
                  // only permit inquiries enhanced to add or delete routes
                  editable={conditionalsEditable}
                  deleteEnabled={editable}
                  isLimited={isLimited}
                  errors={errors}
                  conditional={conditional as WorkflowConditional}
                  usableObjects={usableObjects}
                  jsonSchema={jsonSchema}
                  tokensToDisplayValue={tokensToDisplayValue}
                  tokensToSteps={tokensToSteps}
                  routeIndex={index}
                  deleteRoute={deleteRoute(index)}
                  setConditionalRouteToEdit={setConditionalRouteToEdit}
                />
              </ItemContainer>
            </HoverableDraggable>
            {conditionalsEditable && conditionalRouteToEdit === routeNumber && (
              <EditConditionalPanel
                portalId={EDIT_CONDITIONAL_PANEL_ID}
                blockedRightId='ite-control-panel__container'
                fullscreenTop={FULL_SCREEN_HEADER_HEIGHT_XS + TOOLBAR_HEIGHT}
                conditional={conditional as WorkflowConditional}
                panelTitle={`Edit Route ${routeNumber}`}
                workflowSchema={workflowSchema}
                jsonSchema={jsonSchema}
                tokensToDisplayValue={tokensToDisplayValue}
                onClose={() => {
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  setConditionalRouteToEdit(null);
                }}
                allowEditName
                updateConditional={(conditional) => {
                  const newStep = set(['conditionals', index], conditional, step);
                  dispatch(change(form, `workflowVersions.${name}`, newStep));
                }}
              />
            )}
          </Fragment>
        );
      }),
    [
      conditionals,
      editable,
      conditionalsEditable,
      isLimited,
      errors,
      usableObjects,
      jsonSchema,
      tokensToDisplayValue,
      tokensToSteps,
      deleteRoute,
      setConditionalRouteToEdit,
      conditionalRouteToEdit,
      workflowSchema,
      step,
      dispatch,
      name,
    ],
  );

  const renderRouteSections = useCallback(
    (droppableProvided: DroppableProvided, droppableSnapshot: DroppableStateSnapshot) => (
      <RouteList ref={droppableProvided.innerRef} isDraggingOver={droppableSnapshot.isDraggingOver}>
        {conditionalSections}
        {droppableProvided.placeholder}
      </RouteList>
    ),
    [conditionalSections],
  );

  const lastIndex = conditionals.length - 1;

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='droppable'>{renderRouteSections}</Droppable>
      </DragDropContext>
      <ElseWrapper withPadding={editable}>
        <ElseSection
          conditional={conditionals[lastIndex] as WorkflowElse}
          errors={errors}
          index={lastIndex}
          key={lastIndex}
        />
        <>
          <Spacer height={2} />
          <div>
            <LearnMoreTooltip
              id='workflow-config-conditional__add-new-route-tooltip'
              tooltipContent={generateEnhancedMaxLimitReachedMessage(
                'Routes',
                MAX_ROUTES_PER_BRANCHING_FOR_NON_ENHANCED,
              )}
              onClick={() =>
                handleEnhancedLearnMoreClick({
                  feature: Feature.ConditionalBranch,
                })
              }
              side='top'
              disabled={!routesQuotaMet}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              trigger={
                editable && (
                  <Button
                    type='button'
                    variant='basic'
                    width='auto'
                    id='ite-conditional-panel__new-route-button'
                    onClick={handleAddNewRoute}
                    disabled={routesQuotaMet}
                  >
                    Add New Route
                  </Button>
                )
              }
            />
          </div>
        </>
      </ElseWrapper>
    </>
  );
};

export default WorkflowConfigurationConditional;
